import {
  GridColDef,
  GridInitialState,
  GridRowSelectionModel,
  getGridStringOperators ,
} from "@mui/x-data-grid-premium";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";

import { Alert, Box, Button, CircularProgress, Snackbar } from "@mui/material";

import { RiExternalLinkLine, RiUploadCloud2Line } from "@remixicon/react";
import { Link } from "react-router-dom";

import { useError } from "../../../contexts/ErrorContext";
import { useDataPlorApi } from "../../../hooks/useDataplorApi";
import useApi from "../../../hooks/useGaneshApi";

import { NameRecurrenceType } from "../../../types/Brand";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import GoogleMapsPlaceButton from "../UI/GoogleMapsPlaceButton";
import ExternalLinksButtonGroup from "../../../components/UI/ExternalLinksButtonGroup";
import PlaceDetailsModalCell from "../UI/PlaceDetailsModallCell";

import { LinearProgress } from "@mui/material";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface NameRecurrencesDrillDownProps {
  country_code: string;
  name_recurrence_type: NameRecurrenceType;
  name_like: string;
  suggested_chain_id: string;
}

const CHAIN_SUGGESTION_API_URL: Record<NameRecurrenceType, string> = {
  partially_filled_chains: "/brand_tagging/get_name_recurrences_drilldown",
};
const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [] },
  pagination: { paginationModel: { pageSize: 250, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const columns_def: GridColDef[] = [
  {
    field: "place_id",
    headerName: "Place ID",
    description: "The unique identifier of the place.",
    type: "string",
    width: 200,
  },
  {
    field: "name",
    headerName: "Place Name",
    description: "The name of the place.",
    type: "string",
    width: 250,
  },
  {
    field: "suggested_chain_id",
    headerName: "Suggested Chain ID",
    description: "The suggested chain ID of the place.",
    type: "string",
    width: 250,
  },
  {
    field: "current_chain_id",
    headerName: "Current Chain ID",
    description: "The chain ID currently associated with the place.",
    type: "string",
    width: 250,
  },
  {
    field: "external_links",
    headerName: "External Links",
    description: "View the place in Google maps, Kuebiko or store locator when avaialble.",
    width: 200,
    filterable: true,
    renderCell: (params: any) => (
      <ExternalLinksButtonGroup
        place_id={params.row.place_id}
        google_places_id={params.row.google_places_id}
        store_url={params.row.data_source_browseable_url}
      />
    ),
  },
  {
    field: "website",
    headerName: "Website",
    width: 250,
    renderCell: (params: any) => {
      return params.row.website ? (
        <Link
          to={`${params.row.website}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params.value} <RiExternalLinkLine size="14" />
        </Link>
      ) : (
        "--"
      );
    },
  },
  {
    field: "open_status",
    headerName: "Open Status",
    type: "string",
    width: 100,
  },
  {
    field: "location_name",
    headerName: "Location Name",
    type: "string",
    width: 250,
  },
  {
    field: "location_parent_id",
    headerName: "Location Parent ID",
    type: "string",
    width: 250,
  },
  {
    field: "business_category_id",
    headerName: "Business Category ID",
    type: "string",
    width: 250,
  },
  {
    field: "coordinates",
    headerName: "Coordinates",
    type: "string",
    width: 250,
    valueGetter: (value, row) => {
      let coordinates = row.coordinates;
      return coordinates.lng && coordinates.lat
        ? `(${coordinates.lng}, ${coordinates.lat})`
        : "--";
    },
  },
  {
    field: "address",
    headerName: "Address",
    description: "The address of the place.",
    type: "string",
    width: 300,
  },
  {
    field: "city",
    headerName: "City",
    description: "The city of the place.",
    type: "string",
    width: 200,
  },
  {
    field: "state",
    headerName: "State",
    description: "The state of the place.",
    type: "string",
    width: 150,
  },
  {
    field: "postal_code",
    headerName: "Postal Code",
    description: "The postal code of the place.",
    type: "string",
    width: 150,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    type: "string",
    width: 150,
  },
  {
    field: "confidence_score_v2",
    headerName: "Confidence Score V2",
    type: "number",
    width: 200,
  },
  {
    field: "validity_assessment",
    headerName: "Validity Assessment",
    type: "number",
    width: 200,
  },
  {
    field: "data_last_updated",
    headerName: "Data Last Updated",
    description: "The date this data was retrieved from places.",
    type: "string",
    width: 230,
    sortable: false,
  },
  {
    field: "google_review_count",
    headerName: "Google Review Count",
    type: "number",
    width: 150,
  },
  {
    field: "google_review_count_observation_date",
    headerName: "Google Review Count Observation Date",
    type: "string",
    width: 230,
    sortable: false,
  },
  {
    field: "source_keys",
    headerName: "Sources",
    type: "string",
    width: 200,
    filterable: true,
    filterOperators: getGridStringOperators(),
  
    renderCell: (params: any) => {
      const sourceList = params.row?.source_list;
      if (!sourceList || Object.keys(sourceList).length === 0) return "--";
  
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{Object.keys(sourceList).length}</span>
          <PlaceDetailsModalCell data={sourceList} label="Source List" />
        </Box>
      );
    },
  },  
  {
    field: "google_review_count_history",
    headerName: "Review History",
    width: 120,
    renderCell: (params: any) =>
      params.row?.google_review_count_history ? (
        <PlaceDetailsModalCell
          data={params.row.google_review_count_history}
          label="Review Count History"
        />
      ) : (
        "--"
      ),
  },
  {
    field: "last_observations",
    headerName: "Last Observations",
    width: 120,
    renderCell: (params: any) =>
      params.row?.last_observations ? (
        <PlaceDetailsModalCell
          data={params.row.last_observations}
          label="Last Observations"
        />
      ) : (
        "--"
      ),
  }
];

const NameRecurrencesDrillDown: React.FC<NameRecurrencesDrillDownProps> = ({
  country_code,
  name_recurrence_type,
  name_like,
  suggested_chain_id,
}) => {
  useEffect(() => {
    document.title = `Name Recurrences Tagging: ${name_like}`;
  }, [location]);

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitProgress, setSubmitProgress] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [submittedCount, setSubmittedCount] = useState<number | null>(null);
  const [pendingExitUrl, setPendingExitUrl] = useState<string | null>(null);

  const { postDataplorApi } = useDataPlorApi();
  const { showError } = useError();
  const { apiRequest } = useApi();
  const queryClient = useQueryClient();

  const handleRowSelection = (newSelection: GridRowSelectionModel) => {
    setSelectedRows(newSelection);
  };

  // observation transformation helper
  const mapToObservation = (record: any) => ({
    place_id: record.place_id,
    response: {
      data: {
        attributes: {
          patch: [
            {
              p: "/chain_id",
              v: record.suggested_chain_id,
              o: true,
              c: 0.95,
            },
          ],
        },
      },
    },
  });

  const handleAcceptSuggestion = async () => {
    if (!data || selectedRows.length === 0) return;

    const selectedData = data.filter((row: any) =>
      selectedRows.includes(row.id)
    );

    setIsSubmitting(true);
    setSubmitProgress(0);

    try {
      const total = selectedData.length;
      let completed = 0;

      for (let i = 0; i < selectedData.length; i += 25) {
        const chunk = selectedData.slice(i, i + 25);
        const payload = {
          observations: chunk.map(mapToObservation),
        };

        const response = await postDataplorApi(
          "/v3/admin/manual_observations",
          payload
        );

        const result = await response.json();

        if (Array.isArray(result) && result.length === chunk.length) {
          completed += chunk.length;
          console.log(`Completed: ${completed}/${total}`);
          console.log(result);
          setSubmitProgress((completed / total) * 100);
          await sleep(300);
        } else {
          throw new Error("Unexpected response format");
        }
      }

      setSelectedRows([]);
      setShowSuccessSnackbar(true);
      setSubmittedCount(completed);

      // Invalidate the GET query to refresh data
      queryClient.invalidateQueries({
        queryKey: [
          "name-recurrence",
          name_recurrence_type,
          country_code,
          name_like,
        ],
      });

      // Revert button label after 2.5s
      setTimeout(() => setSubmittedCount(null), 2500);
    } catch (err: any) {
      showError(`Failed to post observations: ${err.message}`);
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
    }
  };
  interface NameRecurrenceRow {
    source_list?: Record<string, any>;
    [key: string]: any; // allows other keys like place_id, name, etc.
  }
  
  // Get Drilldown Data
  const { data, isLoading, error } = useQuery<any, Error>({
    queryKey: [
      "name-recurrence",
      name_recurrence_type,
      country_code,
      name_like,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        country_code: country_code,
        name_recurrence: name_like,
        s_chain_id: suggested_chain_id,
      });

      const response = await apiRequest<any[]>(
        `${
          CHAIN_SUGGESTION_API_URL[name_recurrence_type as NameRecurrenceType]
        }?${params.toString()}`,
        {
          method: "GET",
        }
      );
      return response.data;
    },
    retry: 5,
  })
  const transformedData = (data || []).map((row: NameRecurrenceRow) => {
    const sourceKeys = row.source_list
      ? Object.keys(row.source_list)
      : [];
      const externalKeys: string[] = [];
      if (row.google_places_id) externalKeys.push("google");
      if (row.place_id) externalKeys.push("kuebiko");
      if (row.store_url) externalKeys.push("store");

    return {
      ...row,
      source_keys: sourceKeys.join(", "),
      source_count: sourceKeys.length,
      external_links: externalKeys.join(", "),
    };
  });
  

  if (error) {
    showError(
      `Failed to fetch data for ${name_recurrence_type} name recurrence drill down for name like ${name_like} in ${country_code} .`
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          color={submittedCount !== null ? "success" : "primary"}
          disabled={selectedRows.length === 0 || isSubmitting}
          onClick={handleAcceptSuggestion}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : submittedCount !== null ? (
            `${submittedCount} suggestions submitted`
          ) : (
            <>
              <RiUploadCloud2Line size={16} style={{ marginRight: 4 }} />
              Accept Suggested Chain ID
            </>
          )}
        </Button>

        {selectedRows.length > 0 && !isSubmitting && (
          <Box
            sx={{ fontSize: 14, color: "text.secondary", fontStyle: "italic" }}
          >
            {selectedRows.length} selected
          </Box>
        )}
      </Box>

      {isSubmitting && (
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={submitProgress}
            sx={{ flexGrow: 1 }}
          />
          <Box sx={{ minWidth: 40, fontSize: 14 }}>
            {Math.round(submitProgress)}%
          </Box>
        </Box>
      )}

      <GaneshDataGrid
        data={transformedData}
        loading={isLoading}
        defaultGridState={defaultGridState}
        columns={columns_def}
        gridId={`nameRecurrenceTable-${name_recurrence_type}`}
        checkboxSelection={true}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleRowSelection}
      />

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Observations posted successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NameRecurrencesDrillDown;
